import { api } from "@/lib/http";
import type { FileUploadResult } from "@/types/api";
import { Alert, Box, Button, Stack } from "@mui/material";
import { useRef, useState } from "react";

interface Props {
  url: string | null;
  onUploaded: (file: FileUploadResult) => void;
  onCleared?: () => void;
  height?: number;
}

export function ImageUploader({ url, onUploaded, onCleared, height = 300 }: Props) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleFile(file: File) {
    setBusy(true);
    setErr(null);
    try {
      const result = await api.upload<FileUploadResult>("/api/files", file);
      onUploaded(result);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box>
      <Box
        sx={{
          height,
          width: "100%",
          bgcolor: "grey.100",
          backgroundImage: url ? `url(${url})` : undefined,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          border: 1,
          borderColor: "divider",
        }}
      />
      <input
        ref={ref}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = "";
        }}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button size="small" disabled={busy} onClick={() => ref.current?.click()}>
          {url ? "Change image" : "Upload image"}
        </Button>
        {url && onCleared && (
          <Button size="small" color="error" onClick={onCleared}>
            Delete image
          </Button>
        )}
      </Stack>
      {err && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {err}
        </Alert>
      )}
    </Box>
  );
}
